import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLang } from '../utils/LanguageContext';
import { useAuth } from '../utils/AuthContext';
import { shareWhatsApp } from '../utils/whatsapp';
import { t } from '../utils/translations';
import './HistoryScreen.css';
import Loader from '../components/Loader';

function HistoryScreen() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const { lang } = useLang();
  const { org } = useAuth();
  const tr = t[lang];

  function receiptText(order) {
    const lines = [];
    lines.push(`🌅 ${org?.name || 'StratosPOS'}`);
    lines.push(`${tr.receipt} #${order.id.slice(-5).toUpperCase()} — ${formatDate(order.created_at)}`);
    lines.push('━━━━━━━━━━━');
    (order.order_items || []).forEach(i => {
      lines.push(`${i.product_name} x${i.quantity} — ${i.price * i.quantity} ${tr.currency}`);
    });
    lines.push('━━━━━━━━━━━');
    lines.push(`${tr.total}: ${order.total} ${tr.currency}`);
    lines.push(tr.receiptThanks);
    return lines.join('\n');
  }

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    const { data } = await supabase
      .from('orders')
      .select(`*, order_items(*)`)
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  }

  function toggleExpand(id) {
    setExpanded(prev => prev === id ? null : id);
  }

  function formatDate(str) {
    return new Date(str).toLocaleString('fr-MA', {
      timeZone: 'Africa/Casablanca',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  function isToday(str) {
    const orderDate = new Date(new Date(str).toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    return orderDate.toDateString() === today.toDateString();
  }

  const todayOrders = orders.filter(o => isToday(o.created_at));
  const todayTotal = todayOrders.reduce((sum, o) => sum + parseFloat(o.total), 0);

  if (loading) return <Loader text={tr.loadingHistory} />;

  return (
    <div className="history-screen">

      <div className="stats-bar">
        <div className="stat-card">
          <span className="stat-label">{tr.todayOrders}</span>
          <span className="stat-value">{todayOrders.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{tr.todayRevenue}</span>
          <span className="stat-value">{todayTotal} MAD</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{tr.totalOrders}</span>
          <span className="stat-value">{orders.length}</span>
        </div>
      </div>

      <h3 className="section-title">{tr.orderHistory}</h3>

      {orders.length === 0 && (
        <div className="empty">{tr.noOrders}</div>
      )}

      {orders.map(order => (
        <div key={order.id} className="order-card" onClick={() => toggleExpand(order.id)}>
          <div className="order-header">
            <div>
              <span className="order-id">Order #{order.id.slice(-5).toUpperCase()}</span>
              <span className="order-date">{formatDate(order.created_at)}</span>
              <span className="order-tags">
                {order.order_type === 'takeaway' ? `🥡 ${tr.takeaway}` :
                 order.order_type === 'table' ? `🔢 ${tr.table} ${order.table_label || ''}` :
                 `🍽️ ${tr.dineIn}`}
                {' · '}
                {order.payment_method === 'card' ? `💳 ${tr.payCard}` :
                 order.payment_method === 'carnet' ? `📒 ${tr.payCarnet}` :
                 `💵 ${tr.payCash}`}
              </span>
            </div>
            <div className="order-right">
              <span className="order-total">{order.total} MAD</span>
              <span className="expand-icon">{expanded === order.id ? '▲' : '▼'}</span>
            </div>
          </div>

          {expanded === order.id && (
            <div className="order-items">
              {order.order_items.map(item => (
                <div key={item.id} className="order-item-row">
                  <span>{item.product_name}</span>
                  <span>x{item.quantity}</span>
                  <span>{item.price * item.quantity} MAD</span>
                </div>
              ))}
              <div className="order-item-total">
                <span>{tr.total}</span>
                <span>{order.total} MAD</span>
              </div>
              <button
                className="wa-btn"
                onClick={(e) => { e.stopPropagation(); shareWhatsApp(receiptText(order)); }}
              >
                📱 {tr.sendReceipt}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default HistoryScreen;